import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ComponentProps } from 'react';
import {
  Select,
  Box,
  MaterialSymbol,
  getMaskedNumber,
  withStyles,
  FieldData,
  formattedCurrency,
  Icon,
  AccountFundingOption,
  FundingSource,
  FundingSettlementTime,
  IsTodayDate,
  Skeleton,
} from 'vopay';
import { PaymentFieldDefault } from './types';
import styles from './TransactionCreationFields.module.scss';
import { useClientData } from '@/providers/ClientData.provider';

const classes = withStyles(styles);

type FundingOptionRow = {
  value: FundingSource;
  icon: Extract<ComponentProps<typeof MaterialSymbol>['icon'], 'payments' | 'account_balance' | 'credit_card'>;
  title: string;
  subtitle: string;
  settlementTime: string;
  fee: string;
  isFree: boolean;
  disabled: boolean;
  raw: AccountFundingOption;
};

const ICON_BY_SOURCE: Record<FundingSource, FundingOptionRow['icon']> = {
  balance: 'payments',
  bank: 'account_balance',
  credit: 'credit_card',
};

const ICON_FILL_BY_SOURCE: Record<FundingSource, number> = {
  balance: 0,
  bank: 1,
  credit: 0,
};

type FundingOptionFieldConfig<T> = {
  // Was 'PayFrom' | 'Destination' — widened to any real key of T so a caller with a differently
  // named field (or a wholly different values type, e.g. eLinx's ElinxInfo) can still satisfy
  // FieldData<T>'s own fieldName: Extract<keyof T, string> requirement.
  fieldName: Extract<keyof T, string>;
  translationPrefix: 'PayFrom' | 'Destination';
  labelDefaultValue: string;
  showFee: boolean;
  // PayFrom shows a right-aligned settlement-time badge per option; Destination's subtitle text
  // already states the settlement time, so the badge would just repeat it.
  showSettlementTime: boolean;
  excludeSources?: FundingSource[];
  getSubtitle: (option: AccountFundingOption) => string;
  // PayFrom echoes the live balance amount next to the selected value; Destination doesn't.
  showBalanceInlineSubtitle: boolean;
  // Full visibility condition beyond the shared clientAccounts gate below — each caller's own
  // form shape decides what "scheduled"/"request-not-provide" mean for it (Payments/Collection's
  // PaymentFieldDefault vs eLinx's ElinxInfo have different field names for the same concepts).
  isAdditionallyVisible?: (values: Partial<T>) => boolean;
  // Shown disabled (not hidden) rather than excluded — e.g. PayFrom's balance option when the
  // entered amount exceeds it. Evaluated at render time against the live form values (not the
  // config-build-time values, which run before `ctx` exists).
  isOptionDisabled?: (option: AccountFundingOption, values: Partial<T>) => boolean;
};

// Shared with SinglePaymentForm — a scheduled payment never settles at submit time, so it neither
// shows Pay From nor gets a funding transaction/banner.
export const isPaymentScheduled = (values: Partial<PaymentFieldDefault>): boolean =>
  values.ScheduleType === 'recurring' ||
  !!(values.TakeFromAccountOn && !IsTodayDate(format(new Date(Number(values.TakeFromAccountOn)), 'yyyy-MM-dd')));

// Shared with the funding banner (SinglePaymentForm), so both surfaces word settlement time
// identically.
export const getFundingSettlementTimeLabel = (
  t: (key: string, options: { defaultValue: string; count?: number }) => string,
  settlementTime: FundingSettlementTime,
): string => {
  const tr = (key: string, options: { defaultValue: string; count?: number }) =>
    t(`Fields.PayFrom.${key}`, options);
  if (settlementTime === 'immediately') return tr('instant', { defaultValue: 'Instant' });
  const days = Number(settlementTime.replace('t', ''));
  const key = days === 1 ? 'businessDaysOne' : 'businessDaysOther';
  return tr(key, { count: days, defaultValue: '{{count}} business day' });
};

// Shared with the funding banners (Payments/eLinx) and Pay From — AvailableFunds is the funding
// option's real spendable amount; AccountBalance (which can include reserved/pending funds) is
// only a fallback for options that don't report AvailableFunds at all. Uses ?? rather than || so
// a legitimate zero isn't mistaken for "not reported" and doesn't fall through to AccountBalance.
export const getAvailableBalance = (option?: Pick<AccountFundingOption, 'AvailableFunds' | 'AccountBalance'>): number =>
  Number(option?.AvailableFunds ?? option?.AccountBalance ?? 0);

export const useFundingOptionField = <T,>(
  fundingOptions: AccountFundingOption[],
  isFetchingFundingOptions: boolean,
  config: FundingOptionFieldConfig<T>,
): FieldData<T>[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  const {
    response: { clientAccountsEnabled },
  } = useClientData();
  const {
    fieldName,
    translationPrefix,
    labelDefaultValue,
    showFee,
    showSettlementTime,
    excludeSources,
    getSubtitle,
    isAdditionallyVisible,
    isOptionDisabled,
  } = config;

  const tr = (key: string, options: { defaultValue: string; count?: number }): string =>
    t(`Fields.${translationPrefix}.${key}`, options);

  const getSettlementTime = (settlementTime: FundingSettlementTime) => getFundingSettlementTimeLabel(t, settlementTime);

  const getFeeDisplay = (option: AccountFundingOption) => {
    const feePercent = Number(option.FeePercent || 0);
    const feePerTransaction = Number(option.FeePerTransaction || 0);
    if (feePercent > 0) return `${option.FeePercent}%`;
    if (feePerTransaction > 0) return `${option.Currency} ${formattedCurrency(option.FeePerTransaction || '0')}`;
    return tr('noFee', { defaultValue: 'No fee' });
  };

  const getTitle = (option: AccountFundingOption) => {
    if (option.Source === 'balance') return tr('vopayBalance', { defaultValue: 'VoPay Balance' });
    if (option.Source === 'bank') {
      return `${tr('bankAccount', { defaultValue: 'Bank Account' })} ${option.AccountNumber || ''}`;
    }
    return `${tr('creditCard', { defaultValue: 'Credit Card' })} ${getMaskedNumber(option.CreditCardNumber || '')}`;
  };

  const options: FundingOptionRow[] = fundingOptions
    .filter((option) => !excludeSources?.includes(option.Source))
    .map((option) => ({
      value: option.Source,
      icon: ICON_BY_SOURCE[option.Source],
      title: getTitle(option),
      subtitle: getSubtitle(option),
      settlementTime: getSettlementTime(option.SettlementTime),
      fee: getFeeDisplay(option),
      isFree: Number(option.FeePercent || 0) <= 0 && Number(option.FeePerTransaction || 0) <= 0,
      // Recomputed at render time against live values — see isOptionDisabled's comment.
      disabled: false,
      raw: option,
    }));

  return [
    {
      label: () => tr('label', { defaultValue: labelDefaultValue }),
      fieldName,
      getInputState: () => 'required',
      showField: (values) => {
        return !clientAccountsEnabled && (isAdditionallyVisible?.(values) ?? true);
      },
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<FundingSource>({});
        if (isFetchingFundingOptions) {
          return <Skeleton height={32} />;
        }
        const optionsForRender: FundingOptionRow[] = isOptionDisabled
          ? options.map((o) => ({ ...o, disabled: isOptionDisabled(o.raw, ctx.values) }))
          : options;
        return (
          <Select
            version="2"
            sort={false}
            placeholder={tr('placeholder', { defaultValue: 'Please Select...' })}
            options={optionsForRender}
            optionValue={(o) => o.value}
            disableOptionWhen={(o) => o.disabled}
            {...fieldData}
            renderSelectedValue={(o, { Placeholder }) =>
              o ? (
                <Box ai="center">
                  <div className={classes('paymentName')}>{o.title} </div>
                  {config.showBalanceInlineSubtitle && o.value === 'balance' && (
                    <span style={{ paddingLeft: '5px' }}>· {o.subtitle}</span>
                  )}
                </Box>
              ) : (
                <Placeholder />
              )
            }
            renderOption={(o, { Option, selected, disabled, preventDefault }) => (
              <Option
                onClick={() => {
                  if (disabled) preventDefault();
                }}
              >
                <Box gapSize="12px" ai="center">
                  {selected ? (
                    <MaterialSymbol icon="check" size="20px" color="var(--vopayBlue)" />
                  ) : o.value === 'balance' ? (
                    <Icon width="18px" />
                  ) : (
                    <MaterialSymbol icon={o.icon} size="20px" color="#444" fillAmount={ICON_FILL_BY_SOURCE[o.value]} />
                  )}
                  <Box gapSize="2px" flx="col">
                    <div className={classes('paymentName', 'mediumWeight')}>{o.title}</div>
                    <div className={classes('paymentInfo')}>{o.subtitle}</div>
                  </Box>
                </Box>
                <Box flx="col" ai="fe" gapSize="4px">
                  {showSettlementTime && <div className={classes('smallFont')}>{o.settlementTime}</div>}
                  {showFee && <div className={classes(o.isFree && 'green', 'mediumWeight', 'smallFont')}>{o.fee}</div>}
                </Box>
              </Option>
            )}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
  ];
};
